import { FORM_LINK_SAMPLE_URL, FORM_LINK_PREVIEW_URL } from '@/utils/formsSettings/formLink';

/**
 * MessageTemplateBox — the generic LINK chip (Forms Settings' Form link, docs/plans/2026-10-20-forms-settings-plan.md §2.4, §1f; board
 * MessageCard.dc.html), kept in a mixin so the box's own options stay exactly what the Appointment Reminders work pinned.
 *
 * The generalised sibling of the box's `booking` prop. The box does not interpret the contract: it draws the chip + the picker and hands the choice
 * back through `link-pick`; it stores nothing. The booking chip keeps its own prop / chip / popover / `booking-link` event untouched (its 30 tests and the
 * node-test pins on the box's source pass unedited); what the two share is the machinery around them — the token-once insert (`insertToken`), the
 * sample / preview URLs the counter and the previews substitute, the unknown-{{variable}} allowance, and the open / outside-click / Escape discipline.
 *
 * THE MIXIN READS these members of the box: `value`, `locked`, `isBlank`, `error`, `caret`, `$refs.field`, `insertToken(name, { lead })`.
 *
 * THE CONTRACT (`link` prop):
 *   token          the variable the chip inserts, without braces ('form_link'); absent / blank = this box has no link chip
 *   chipLabel      'Form link'            `required` adds "(required)" after it
 *   title          the popover's question ('Which form should this link open?')
 *   searchPlaceholder   'Search forms and packets…'
 *   items          [{ kind:'form'|'packet', id, name, meta?, tag? }]   Packets are listed first, then Forms; the search matches name + tag
 *   itemsLoaded / itemsLoading   whether items is the real list yet (a saved choice missing from a LOADED list gets the "deleted" warning)
 *   value          { kind, id, name } | null   the saved choice
 *   sampleUrl / previewUrl      what the SMS counter measures / the previews draw for the token (default: the 33-char form-link sample)
 *
 * BEHAVIOUR: the chip is first in the chip row; a click inserts ONE token at the caret only when the text has none (a space before it when it would
 * touch a word), then opens the picker — with the token already there it only (re)opens the picker, and the chip is also its toggle. The picker
 * is a popover under the chip (search + groups + keyboard ↑ ↓ Enter Esc) and a bottom sheet with 52px rows at phone width. Under the box:
 * "Link opens: <name> · Change" / "no form picked yet · Choose a form" / the "deleted — pick another" warning.
 */
export default {
  props: {
    link: { type: Object, default: () => ({}) },
  },
  data() {
    // the picker: open, what is typed in its search, the highlighted row, and whether it is drawn as a phone bottom sheet
    return { linkOpen: false, linkQuery: '', linkHi: 0, linkSheet: false };
  },
  computed: {
    lk() { return this.link || {}; },
    linkToken() { return typeof this.lk.token === 'string' ? this.lk.token.trim() : ''; },
    // The chip exists only in a box the page gave a link to (a token). The box knows nothing else about what the link opens.
    linkActive() { return this.linkToken !== ''; },
    // The unknown-{{variable}} check lets {{form_link}} through in THIS box only (mirrors `booking.allowed`).
    linkIsFormLink() { return this.linkToken === 'form_link'; },
    linkRequired() { return this.lk.required === true; },
    linkChipLabel() { return typeof this.lk.chipLabel === 'string' && this.lk.chipLabel.trim() ? this.lk.chipLabel.trim() : 'Form link'; },
    linkTitle() { return typeof this.lk.title === 'string' && this.lk.title.trim() ? this.lk.title.trim() : 'Which form should this link open?'; },
    linkSearchPlaceholder() {
      return typeof this.lk.searchPlaceholder === 'string' && this.lk.searchPlaceholder.trim() ? this.lk.searchPlaceholder.trim() : 'Search forms and packets…';
    },
    linkSampleUrl() { return typeof this.lk.sampleUrl === 'string' && this.lk.sampleUrl ? this.lk.sampleUrl : FORM_LINK_SAMPLE_URL; },
    linkPreviewUrl() { return typeof this.lk.previewUrl === 'string' && this.lk.previewUrl ? this.lk.previewUrl : FORM_LINK_PREVIEW_URL; },
    linkItems() {
      return (Array.isArray(this.lk.items) ? this.lk.items : [])
        .filter(i => i && typeof i === 'object' && (i.kind === 'form' || i.kind === 'packet') && i.id !== undefined && i.id !== null && String(i.id) !== '');
    },
    linkItemsLoaded() { return this.lk.itemsLoaded === true; },
    linkItemsLoading() { return this.lk.itemsLoading === true; },
    // The saved choice, or null (nothing picked yet).
    linkValue() {
      const v = this.lk.value;
      return v && typeof v === 'object' && (v.kind === 'form' || v.kind === 'packet') && v.id !== undefined && v.id !== null && String(v.id) !== '' ? v : null;
    },
    linkTitleId() { return `mtb-${this._uid}-lk-title`; },
    showLinkChip() { return !this.locked && this.linkActive; },
    // How many copies of the token the message holds (spaces inside the braces tolerated, as the sender tolerates them).
    linkTokenCount() {
      if (!this.linkActive) return 0;
      const escaped = this.linkToken.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const found = String(this.value || '').match(new RegExp(`\\{\\{\\s*${escaped}\\s*\\}\\}`, 'g'));
      return found ? found.length : 0;
    },
    hasLinkToken() { return this.linkTokenCount > 0; },
    // The saved choice as the LOADED list knows it (a rename shows at once); null when it is not in the list.
    linkPicked() {
      const v = this.linkValue;
      return v ? (this.linkItems.find(i => i.kind === v.kind && String(i.id) === String(v.id)) || null) : null;
    },
    // A saved form / packet the (loaded) list no longer has: deleted. Never guessed before the list is known.
    linkRemoved() { return !!this.linkValue && this.linkItemsLoaded && !this.linkPicked; },
    linkNoneYet() { return !this.linkValue; },
    showLinkReadout() { return this.showLinkChip && this.hasLinkToken && !this.linkRemoved; },
    showLinkWarning() { return this.showLinkChip && this.hasLinkToken && this.linkRemoved; },
    linkReadoutLabel() {
      if (!this.linkValue) return '';
      const saved = typeof this.linkValue.name === 'string' ? this.linkValue.name.trim() : '';
      return (this.linkPicked && this.linkPicked.name) || saved || 'Your form';
    },
    // The page's own error ("Add the Form link …") is sitting under a message that has none: offer to add it.
    linkAddable() { return this.showLinkChip && !this.hasLinkToken && !this.isBlank; },
    linkMissing() { return this.linkRequired && !this.hasLinkToken && !!this.error; },
    // The items the search leaves, in the order they are listed and highlighted: Packets first, then Forms.
    linkFlat() {
      const q = this.linkQuery.trim().toLowerCase();
      const match = i => !q || String(i.name || '').toLowerCase().includes(q) || String(i.tag || '').toLowerCase().includes(q);
      return ['packet', 'form'].reduce((all, kind) => all.concat(this.linkItems.filter(i => i.kind === kind && match(i))), []);
    },
    linkGroups() {
      const v = this.linkValue;
      return [{ key: 'packet', heading: 'Packets' }, { key: 'form', heading: 'Forms' }]
        .map(g => ({
          key: g.key,
          heading: g.heading,
          items: this.linkFlat
            .map((item, index) => ({ item, index, selected: !!v && item.kind === v.kind && String(item.id) === String(v.id) }))
            .filter(o => o.item.kind === g.key),
        }))
        .filter(g => g.items.length > 0);
    },
    linkNoMatch() { return this.linkFlat.length === 0 && !this.linkItemsLoading; },
    linkNoMatchText() {
      const q = this.linkQuery.trim();
      return q ? `No forms or packets match “${q}”.` : 'No forms or packets yet.';
    },
  },
  watch: {
    // The Form link picker listens for Escape and clicks outside only while it is open, and never outlives the chip (a message switched off).
    linkOpen(open) {
      if (open) {
        document.addEventListener('mousedown', this.onLinkOutside, true);
        document.addEventListener('keydown', this.onLinkEscape, true);
      } else {
        this.removeLinkListeners();
      }
    },
    showLinkChip(shown) { if (!shown) this.linkOpen = false; },
    linkHi() { this.$nextTick(this.keepLinkHi); },
  },
  beforeDestroy() {
    this.removeLinkListeners();
  },
  methods: {
    // This unknown-{{variable}} is the link's own token, which THIS box allows (a box with no form_link link flags it as before).
    isOwnLinkToken(unknown) {
      return this.linkIsFormLink && !!unknown && typeof unknown.name === 'string' && unknown.name.trim() === 'form_link';
    },

    // The chip: ONE token goes in at the caret only when the text has none (a space before it when it would touch a word), then the popover asks
    // which form / packet it opens. A second click while the token is there just reopens the choice, never a second token; the chip is also the
    // popover's toggle. The choice is the page's to store (`link-pick`); nothing is saved here.
    onLinkChip() {
      if (this.linkOpen) {
        this.closeLinkPicker(true);
        return;
      }
      if (!this.hasLinkToken) this.insertToken(this.linkToken, { lead: true });
      this.openLinkPicker();
    },

    // A phone-width viewport gets the bottom sheet (52px rows, board 4c); anything else the popover under the chip.
    isPhoneWidth() {
      try {
        return !!(window.matchMedia && window.matchMedia('(max-width: 640px)').matches);
      } catch (e) {
        return false;
      }
    },

    openLinkPicker() {
      this.linkSheet = this.isPhoneWidth();
      this.linkQuery = '';
      const at = this.linkFlat.findIndex(i => !!this.linkValue && i.kind === this.linkValue.kind && String(i.id) === String(this.linkValue.id));
      this.linkHi = at === -1 ? 0 : at;
      this.linkOpen = true;
      // After insertToken's own refocus of the message, so the search box keeps the focus (keyboard users land on it).
      this.$nextTick(() => {
        const el = this.$refs.lkSearch;
        if (el && typeof el.focus === 'function') el.focus({ preventScroll: true });
        this.keepLinkHi();
      });
    },

    pickLink(item) {
      if (!item) return;
      this.$emit('link-pick', { kind: item.kind, id: item.id, name: item.name || '' });
      this.closeLinkPicker(true);
    },

    // `refocus`: Escape and a pick hand the focus back to the message (the caret as it was); a click elsewhere does not.
    closeLinkPicker(refocus) {
      if (!this.linkOpen) return;
      this.linkOpen = false;
      this.linkQuery = '';
      if (!refocus) return;
      this.$nextTick(() => {
        const el = this.$refs.field;
        if (!el) return;
        el.focus({ preventScroll: true });
        const len = String(this.value || '').length;
        const c = this.caret || { start: len, end: len };
        el.setSelectionRange(Math.min(c.start, len), Math.min(c.end, len));
      });
    },

    onLinkQuery(event) {
      this.linkQuery = event.target.value;
      this.linkHi = 0; // the best match is the first row
    },

    // Up / Down move the highlight (wrapping), Enter picks it. Escape is handled by onLinkEscape (a document listener), whatever has the focus.
    onLinkKey(event) {
      const n = this.linkFlat.length;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (n === 0) return;
        const at = Math.min(this.linkHi, n - 1);
        this.linkHi = event.key === 'ArrowDown' ? (at + 1) % n : (at - 1 + n) % n;
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (n > 0) this.pickLink(this.linkFlat[Math.min(this.linkHi, n - 1)]);
      }
    },

    // Keep the highlighted row inside the list's visible part.
    keepLinkHi() {
      const list = this.$refs.lkList;
      const row = list && list.querySelector ? list.querySelector('[data-hi="1"]') : null;
      if (!row || typeof row.offsetTop !== 'number') return;
      const top = row.offsetTop;
      const bottom = top + row.offsetHeight;
      if (top < list.scrollTop + 28) list.scrollTop = Math.max(0, top - 30);
      else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight + 8;
    },

    onLinkOutside(event) {
      const t = event.target;
      const inside = [this.$refs.lkWrap, this.$refs.lkChange].some(el => el && typeof el.contains === 'function' && el.contains(t));
      if (!inside) this.closeLinkPicker(false);
    },

    // Escape closes ONLY the picker: the event is stopped here so a full-screen editor around this box does not also treat it as "close".
    onLinkEscape(event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      event.stopPropagation();
      event.preventDefault();
      this.closeLinkPicker(true);
    },

    removeLinkListeners() {
      document.removeEventListener('mousedown', this.onLinkOutside, true);
      document.removeEventListener('keydown', this.onLinkEscape, true);
    },
  },
};
